import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	inject,
} from '@angular/core'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { PostLineHex } from '@modules/trading-view-module/constants/post-line-hex'
import { PreLineHex } from '@modules/trading-view-module/constants/pre-line-hex'
import { TimeframesWithPrePostLines } from '@modules/trading-view-module/constants/timeframes-with-pre-post-lines'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { TradingViewHelperService } from '@modules/trading-view-module/services/trading-view-helper.service'
import { TradingViewDatafeedService } from '@modules/trading-view-module/services/trading-view-datafeed.service'
import { TranslateService } from '@ngx-translate/core'
import {
	Bar,
	CustomTimezoneId,
	EntityId,
	IChartingLibraryWidget,
	IChartWidgetApi,
	ILineDataSourceApi,
	ISubscription,
	PricedPoint,
	ResolutionString,
	TimeFrameValue,
	widget,
} from '@public/trading-view/charting_library'
import { MsInMinute } from '@shared/constants/times/ms-in-minute'
import { interval, Subject, takeUntil, tap } from 'rxjs'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { SocketPricesStocksService } from '@modules/stocks-module/socket-prices/services/socket-prices-stocks.service'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { ThemeService } from '@modules/theme-module/services/theme.service'
import { LanguageService } from '@modules/lang-module/services/language.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-trading-view',
	templateUrl: './trading-view.component.html',
	styleUrl: './trading-view.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [TradingViewDatafeedService],
})
export class TradingViewComponent implements OnInit, OnDestroy, AfterViewInit {
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly msInMinute: number = MsInMinute
	protected readonly preLineHex: string = PreLineHex
	protected readonly postLineHex: string = PostLineHex
	protected readonly timeFramesWithPrePostLines: TradingViewTimeframesEnum[] = TimeframesWithPrePostLines
	protected readonly surface100: string = '--surface-100'
	protected readonly line200: string = '--line-200'
	protected readonly text400: string = '--text-400'

	@ViewChild('tvContainer', { static: true }) private tvContainer: ElementRef<HTMLDivElement> | null = null

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _languageService: LanguageService = inject<LanguageService>(LanguageService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _tradingViewDatafeedService: TradingViewDatafeedService =
		inject<TradingViewDatafeedService>(TradingViewDatafeedService)
	private readonly _tradingViewHelperService: TradingViewHelperService =
		inject<TradingViewHelperService>(TradingViewHelperService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _socketPricesStocksService: SocketPricesStocksService =
		inject<SocketPricesStocksService>(SocketPricesStocksService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)

	private _prePostLineEntityId: EntityId | null = null
	private _theme: ThemeEnum = this.themeEnum.LIGHT
	private _liveTicker: LiveTickerInterface | null = null
	private _tradingView: IChartingLibraryWidget | null = null
	private _tradingViewLanguage: LanguageEnum = this.languageEnum.ENGLISH
	private _symbolChangedSub: ISubscription<() => void> | null = null
	private _timeframeChangedSub: ISubscription<
		(interval: ResolutionString, timeFrameParameters: { timeframe?: TimeFrameValue }) => void
	> | null = null
	private _isChartReady: boolean = false
	private _isLoadingChart: boolean = false

	get chartOverrides(): Record<string, string> {
		const style: CSSStyleDeclaration = getComputedStyle(document.body)
		const surface100: string = style.getPropertyValue(this.surface100).trim()
		const line200: string = style.getPropertyValue(this.line200).trim()
		const text400: string = style.getPropertyValue(this.text400).trim()

		return {
			'paneProperties.background': surface100,
			'paneProperties.backgroundType': 'solid',
			'paneProperties.separatorColor': line200,
			'paneProperties.horzGridProperties.color': line200,
			'paneProperties.vertGridProperties.color': line200,
			'scalesProperties.textColor': text400,
		}
	}
	private _autoSaveHandler(): void {
		if (!this._tradingView) return
		this._tradingView.save((state: object): void => {
			this._tradingViewHelperService.setTradingViewState(state)
		})
	}

	private _symbolChangedHandler(): void {
		this._removePrePostLine()
		this._prePostLineEntityId = null
		this._checkAndDrawPrePostLines()
		this._getLastBarDay()
	}

	private _initTimeframeAndPeriod(): void {
		if (!this._tradingView) return
		const period: TimeFrameValue | null = this._tradingViewHelperService.tradingViewPeriod$.getValue()
		const timeframe: TradingViewTimeframesEnum = this._tradingViewHelperService.tradingViewTimeframe$.getValue()
		if (period) {
			this._tradingView.activeChart().setTimeFrame({ val: period, res: timeframe as ResolutionString })
		}
	}

	private _timeframeChangedHandler(timeframe: ResolutionString, period: { timeframe?: TimeFrameValue }): void {
		if (!this._tradingView) return

		this._tradingViewHelperService.setTradingViewTimeframe(timeframe as TradingViewTimeframesEnum)
		this._tradingViewHelperService.setTradingViewPeriod(period.timeframe)
		this._checkAndDrawPrePostLines()
	}

	private _listenTheme(): void {
		this._themeService.theme$.pipe(takeUntil(this._destroy$)).subscribe((theme: ThemeEnum): void => {
			this._theme = theme
			this._tradingView?.changeTheme(theme).then((): void => {
				this._tradingView?.applyOverrides(this.chartOverrides)
				this._applyScalesBackground()
			})
		})
	}

	private _initLiveTicker(): void {
		this._liveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((liveTicker: LiveTickerInterface | null): void => {
				this._liveTicker = liveTicker
				if (this._isChartReady) {
					this._tradingView?.activeChart().setSymbol(liveTicker?.ticker || '', (): void => {
						this._checkAndDrawPrePostLines()
					})
				} else {
					this._initTradingView()
				}
			})
	}

	private _listenSocketPricesStocks(): void {
		this._socketPricesStocksService.socketPricesStocks$
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: SocketStocksType[]): void => {
				this._tradingViewDatafeedService.socketPricesStocks$.next(response)
			})
	}

	private _listenLanguage(): void {
		this._languageService.language$.pipe(takeUntil(this._destroy$)).subscribe((lang: LanguageEnum): void => {
			this._tradingViewLanguage = lang
			this._destroyTradingView()
			this._initTradingView()
		})
	}

	private _getLastBarDay(): void {
		if (
			this._marketStatusHelperService.marketStatus === this.marketStatusEnum.marketClose ||
			this._isDailyOrAbove(
				this._tradingViewHelperService.tradingViewTimeframe$.getValue() as TradingViewTimeframesEnum,
			) ||
			!this._liveTicker
		)
			return

		this._tradingViewHelperService
			.getLastBarDay(this._liveTicker?.ticker)
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: Bar | null): void => {
				if (!response) return

				const barKey: string = `${this._liveTicker?.ticker}_1D`
				this._tradingViewHelperService.lastBars.set(barKey, response)
			})
	}

	private _isDailyOrAbove(currentResolution: TradingViewTimeframesEnum): boolean {
		return this.timeFramesWithPrePostLines.includes(currentResolution)
	}

	private _removePrePostLine(): void {
		if (!this._tradingView || !this._prePostLineEntityId) return

		this._tradingView.activeChart().removeEntity(this._prePostLineEntityId)
		this._prePostLineEntityId = null
	}

	private _startTimerRestartTradingView(minutes: number): void {
		interval(minutes * this.msInMinute)
			.pipe(
				takeUntil(this._destroy$),
				tap((): void => {
					this._destroyTradingView()
					this._initTradingView()
				}),
			)
			.subscribe()
	}

	private _checkAndDrawPrePostLines(): void {
		if (!this._liveTicker) return

		const timeframe: TradingViewTimeframesEnum | undefined = this._tradingView
			?.activeChart()
			.resolution() as TradingViewTimeframesEnum
		if (!timeframe) return

		if (this._isDailyOrAbove(timeframe)) {
			const marketStatus: MarketStatusEnum | null = this._marketStatusHelperService.marketStatus

			if (marketStatus === this.marketStatusEnum.preMarket) {
				this._drawPrePostLine(this._translateService.instant('tradingView.pre'), this.preLineHex)
			} else if (marketStatus === this.marketStatusEnum.postMarket) {
				this._drawPrePostLine(this._translateService.instant('tradingView.post'), this.postLineHex)
			}
		} else {
			this._removePrePostLine()
		}
	}

	private _applyScalesBackground(): void {
		if (!this._tradingView) return

		const surface100: string = getComputedStyle(document.body).getPropertyValue(this.surface100).trim()

		const iframe: HTMLIFrameElement | null = this.tvContainer?.nativeElement.querySelector('iframe') ?? null

		if (!iframe?.contentDocument) return

		const docEl: HTMLElement = iframe.contentDocument.documentElement

		docEl.style.setProperty('--tv-color-pane-background', surface100)
		docEl.style.setProperty('--tv-color-platform-background', surface100)
	}

	private _onChartReady(): void {
		if (!this._tradingView) return

		this._tradingView.onChartReady((): void => {
			this._symbolChangedSub = this._tradingView!.activeChart().onSymbolChanged()
			this._symbolChangedSub.subscribe(null, (): void => this._symbolChangedHandler())

			this._initTimeframeAndPeriod()

			this._timeframeChangedSub = this._tradingView!.activeChart().onIntervalChanged()
			this._timeframeChangedSub.subscribe(
				null,
				(timeframe: ResolutionString, period: { timeframe?: TimeFrameValue }): void =>
					this._timeframeChangedHandler(timeframe, period),
			)

			this._tradingView?.subscribe('onAutoSaveNeeded', (): void => this._autoSaveHandler())

			this._startTimerRestartTradingView(30)
			this._tradingView?.changeTheme(this._theme).then((): void => {
				this._tradingView?.applyOverrides(this.chartOverrides)
				this._applyScalesBackground()
			})

			this._isChartReady = true
			this._isLoadingChart = false
		})
	}

	private _drawPrePostLine(text: string, color: string): void {
		if (!this._tradingView) return

		let line: ILineDataSourceApi | null = null
		const chart: IChartWidgetApi = this._tradingView.activeChart()
		const point: PricedPoint = {
			time: chart.getVisibleRange().from,
			price: this._liveTicker?.marketStatusPrices?.price ?? 0,
		}

		if (this._prePostLineEntityId) {
			line = chart.getShapeById(this._prePostLineEntityId) ?? null
		}

		if (line) {
			line.setPoints([point])
			line.setProperties({
				overrides: {
					linecolor: color,
					fontsize: 12,
					showLabel: true,
					horzLabelsAlign: 'right',
					vertLabelsAlign: 'middle',
					linewidth: 2,
					textcolor: color,
					text: text,
					lock: true,
				},
			})
		} else {
			this._prePostLineEntityId = chart.createShape(point, {
				shape: 'horizontal_line',
				overrides: {
					linecolor: color,
					fontsize: 12,
					showLabel: true,
					horzLabelsAlign: 'right',
					vertLabelsAlign: 'middle',
					linewidth: 2,
					textcolor: color,
					text: text,
					lock: true,
				},
				lock: true,
				disableSelection: true,
				disableSave: true,
				disableUndo: true,
			})
		}
	}

	private _initTradingView(): void {
		if (!this.tvContainer?.nativeElement || this._isLoadingChart || this._isChartReady || !this._liveTicker) return

		const tradingViewState: object | null = this._tradingViewHelperService.tradingViewState$.getValue()

		this._isLoadingChart = true
		this._tradingView = new widget({
			custom_css_url: '../styles/tradingview-fixes.css',
			symbol: this._liveTicker?.ticker ?? '',
			datafeed: {
				onReady: this._tradingViewDatafeedService.onReady.bind(this._tradingViewDatafeedService),
				resolveSymbol: this._tradingViewDatafeedService.resolveSymbol.bind(this._tradingViewDatafeedService),
				getBars: this._tradingViewDatafeedService.getBars.bind(this._tradingViewDatafeedService),
				searchSymbols: this._tradingViewDatafeedService.searchSymbols.bind(this._tradingViewDatafeedService),
				subscribeBars: this._tradingViewDatafeedService.subscribeBars.bind(this._tradingViewDatafeedService),
				unsubscribeBars: this._tradingViewDatafeedService.unsubscribeBars.bind(
					this._tradingViewDatafeedService,
				),
			},
			theme: this._theme,
			interval: this._tradingViewHelperService.tradingViewTimeframe$.getValue() as ResolutionString,
			container: this.tvContainer.nativeElement,
			library_path: '/trading-view/charting_library/',
			locale: this._tradingViewLanguage,
			auto_save_delay: 1,
			...(tradingViewState && { saved_data: tradingViewState }),
			timezone: 'America/New_York',
			custom_timezones: [
				{
					id: 'Kazakhstan/Almaty' as CustomTimezoneId,
					alias: 'Etc/GMT-5',
					title: 'Astana',
				},
			],
			fullscreen: false,
			autosize: true,
			disabled_features: [
				'display_market_status',
				'header_saveload',
				'symbol_info',
				'header_symbol_search',
				'use_localstorage_for_settings',
			],
			enabled_features: [
				'side_toolbar_in_fullscreen_mode',
				'header_in_fullscreen_mode',
				'timezone_menu',
				'header_chart_type',
			],
			overrides: this.chartOverrides,
		})

		this._onChartReady()
	}

	private _destroyTradingView(): void {
		if (!this._tradingView) {
			return
		}

		this._isChartReady = false
		this._isLoadingChart = false

		if (this._symbolChangedSub) {
			this._symbolChangedSub.unsubscribe(null, (): void => this._symbolChangedHandler())
			this._symbolChangedSub = null
		}

		if (this._timeframeChangedSub) {
			this._timeframeChangedSub.unsubscribe(
				null,
				(timeframe: ResolutionString, period: { timeframe?: TimeFrameValue }): void =>
					this._timeframeChangedHandler(timeframe, period),
			)
			this._timeframeChangedSub = null
		}

		this._tradingView.unsubscribe('onAutoSaveNeeded', (): void => this._autoSaveHandler())

		this._tradingView.remove()
		this._tradingView = null
	}

	ngAfterViewInit(): void {
		this._initTradingView()
	}

	ngOnInit(): void {
		this._listenTheme()
		this._initLiveTicker()
		this._listenSocketPricesStocks()
		this._listenLanguage()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
		this._destroyTradingView()
	}
}
